import React, { useState } from 'react';
import './Builder.css';

function ARMark({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon points="24,2 44,13 44,35 24,46 4,35 4,13" fill="none" stroke="#00d4ff" strokeWidth="1.5"/>
      <polygon points="24,7 39,15.5 39,32.5 24,41 9,32.5 9,15.5" fill="rgba(0,212,255,0.06)" stroke="rgba(0,212,255,0.3)" strokeWidth="0.75"/>
      <text x="24" y="30" textAnchor="middle" fontFamily="Bebas Neue, sans-serif" fontSize="18" fill="#00d4ff" letterSpacing="1">AR</text>
    </svg>
  );
}

const SECTIONS = [
  {
    num: '01',
    label: '// Flagship Product',
    title: 'ARIA — AI Research & Intelligence Assistant',
    desc: 'A full-stack voice AI legal assistant built from scratch in Python. ARIA is the core of the A.R. Pro Se infrastructure — a production system that listens, reasons, acts, and speaks.',
    accent: 'green',
    items: [
      { title: 'Voice-first architecture', detail: 'Real-time voice interaction via PyAudio and ElevenLabs TTS. Wake-word activation, push-to-talk, and continuous conversation modes. Custom voice clone for document narration and court prep rehearsal.' },
      { title: 'Multi-LLM orchestration layer', detail: 'Integrates with a variety of frontier LLMs via structured tool use and multi-turn conversation management. Handles complex reasoning chains across legal research, document analysis, and strategic planning — model-agnostic by design.' },
      { title: '11+ custom MCP tool integrations', detail: 'Model Context Protocol servers for court docket scraping, document management, calendar integration, evidence chain tracking, financial analysis, and automated filing workflows.' },
      { title: 'Native macOS application', detail: 'SwiftUI Mac app built via Claude Code with live waveform visualization, menu bar integration, and system-level audio routing. Full native experience, not a web wrapper.' },
    ],
    tags: ['Python', 'Multi-LLM', 'MCP Protocol', 'PyAudio', 'ElevenLabs', 'SwiftUI', 'API Orchestration'],
  },
  {
    num: '02',
    label: '// Data Intelligence',
    title: 'Litigation Analytics Engine',
    desc: 'A multi-agent system that transformed raw court data into strategic intelligence at a scale no solo practitioner has attempted before.',
    accent: 'red',
    items: [
      { title: 'Statewide docket analysis', detail: 'Scraped and analyzed 113,162 cases and 3,446,855 docket entries across 9 judicial districts in Connecticut. Built automated pipelines for continuous data collection and pattern detection.' },
      { title: 'Judicial decision modeling', detail: 'Processed 174,661+ grant/deny decisions to identify judicial tendencies, ruling patterns, and statistical anomalies across sealed and unsealed cases.' },
      { title: 'Financial intelligence', detail: 'Mapped complex corporate structures through SEC EDGAR filings, Canadian SEDI records, and public disclosure databases. Automated entity tracing across international jurisdictions.' },
      { title: 'Opposing counsel analytics', detail: 'Filing pattern analysis, success rate modeling, and behavioral prediction across thousands of historical cases. Strategic intelligence delivered before each hearing.' },
    ],
    tags: ['Python', 'Web Scraping', 'Data Pipelines', 'LLM APIs', 'Vector Search', 'Embeddings'],
  },
  {
    num: '03',
    label: '// Secure Infrastructure',
    title: 'Local LLM & Privacy-First AI',
    desc: "When the data is too sensitive for the cloud, you build your own. A self-hosted inference stack designed around one principle: privileged information never leaves the machine.",
    accent: 'cyan',
    items: [
      { title: 'Apple Silicon inference', detail: 'Mac Studio M4 Ultra with 192GB unified memory running Kimi K2.5 self-hosted on-metal alongside Llama, Mistral, and DeepSeek via MLX. Data integrity and privacy by design — privileged information never leaves the machine.' },
      { title: 'Local speech-to-text', detail: 'Whisper deployed locally for transcription of 6-hour courtroom recordings in approximately 30 minutes. Zero cloud dependency for sensitive audio.' },
      { title: 'Speaker diarization', detail: 'PyAnnote pipeline separating judge, counsel, and witness voices from multi-speaker courtroom recordings. Automated attribution and structured output.' },
      { title: 'Semantic document search', detail: 'Local embeddings and vector search across every document ever filed. Instant retrieval of relevant precedent, prior statements, and contradictions across thousands of pages.' },
    ],
    tags: ['MLX', 'Apple Silicon', 'Whisper', 'PyAnnote', 'Kimi K2.5', 'Llama', 'Mistral', 'DeepSeek', 'Vector Search'],
  },
  {
    num: '04',
    label: '// Product Design',
    title: 'Enterprise Software & Platform Design',
    desc: 'Before building AI systems, I was designing enterprise software workflows and shaping products that are still in production today.',
    accent: 'gold',
    items: [
      { title: 'ModMed EMR — founding beta user & product contributor', detail: 'Selected as one of the first practices to beta test the ModMed (Modernizing Medicine) EMR platform. Collaborated directly with the development team to design features still in production — including pre-populated peel-and-stick biopsy specimen labels generated from lab requisitions.' },
      { title: 'Multi-system implementation specialist', detail: 'Led implementation and migration of EMR, CRM, and practice management systems across multiple medical practices. Designed SOPs, trained teams of 10-20 staff, and built workflows that increased revenue by up to 166%.' },
      { title: 'Automated operations pipelines', detail: 'Certified letter automation pipeline — template to USPS tracking number with zero human intervention. Transcript cleanup systems for livestreams, meetings, and voice memos into structured documents.' },
    ],
    tags: ['ModMed', 'EMR/CRM/PMS', 'SOP Design', 'Staff Training', 'Workflow Automation'],
  },
  {
    num: '05',
    label: '// Creative Production',
    title: 'AI-Powered Creative Work',
    desc: "The same infrastructure that powers litigation analytics also composes music, produces animated films, and designs artisan donut menus. This is what 200M tokens/month looks like when a human being is behind the keyboard.",
    accent: 'purple',
    items: [
      { title: '"Irish K-Pop Demon Hunters" — animated film', detail: "A 30-minute animated movie adventure with original songs and a companion storybook. Written, scored, and produced for my children using AI-assisted composition, narrative structuring, and visual development." },
      { title: 'Three-volume musical', detail: "A full-length original musical — three volumes of songs, libretto, and narrative arc. AI-assisted composition, arrangement, and iterative refinement across dozens of songs and scenes." },
      { title: 'Reflective autobiography through the lens of Westworld', detail: "A long-form creative work using the show's framework of loops, consciousness, and narrative control as the structure for examining personal experience." },
      { title: 'Labubu game theory donut collection', detail: 'A full artisan doughnut menu for a concept shop, themed around collectible culture and game theory. Ube-Ricotta Doughnuts. The Gold Leaf Greenwich Doughnut. Matcha-black sesame crullers. Every recipe developed, every name conceptualized, every price point modeled.' },
    ],
    tags: ['Suno AI', 'ElevenLabs', 'LLM APIs', 'Voice Cloning', 'Narrative Design', 'Recipe Engineering'],
  },
  {
    num: '06',
    label: '// Operational Track Record',
    title: 'Startups, Scale, & Stakeholder Management',
    desc: 'A decade of building businesses, managing complex stakeholders, and driving measurable revenue outcomes — from celebrity medical practices to sustainable transportation.',
    accent: 'gold',
    items: [
      { title: 'MOTEV — Co-founded with Morgan Freeman', detail: 'Co-founded a sustainable EV transportation company targeting ultra-high-net-worth clients. Raised $5M+ in capital, built a 10,000 sq ft facility, hired and trained 10+ staff, and secured state licensure — all in under 6 months.' },
      { title: 'Medical practice revenue optimization', detail: 'Grew surgical revenue from $70K to $200K+/month at NassifMD (Dr. Paul Nassif, Botched!) with a 90%+ consultation close rate. Increased client practice revenue by up to 166% through technology-driven workflow redesigns.' },
      { title: 'Multi-practice portfolio management', detail: 'Managed billing operations across 9 anesthesia practices and surgical centers simultaneously. Increased collection rates by 10%+ in under 12 months. Negotiated insurer contracts at rates up to 85% of billed charges.' },
      { title: 'Family office operations', detail: 'Operational liaison to a multi-billion-dollar family office. Quarterly financial report review across public and private holdings. Insurance portfolio management, vendor coordination, and investment opportunity identification.' },
    ],
    tags: ['COO', 'Revenue Optimization', 'Capital Raising', 'Staff Training', 'Portfolio Management', 'Financial Services'],
  },
];

const STACK_GROUPS = [
  { label: 'LLM Orchestration', items: ['Multi-LLM API Integration', 'MCP Protocol (11+ custom)', 'Structured Tool Use', 'Multi-Agent Architecture'] },
  { label: 'AI/ML Stack', items: ['Python', 'PyAudio', 'ElevenLabs', 'Whisper (local)', 'PyAnnote', 'MLX', 'Vector Search', 'Embeddings'] },
  { label: 'Models', items: ['Kimi K2.5 (self-hosted)', 'Llama', 'Mistral', 'DeepSeek', 'GPT-4o', 'Gemini Ultra', 'Grok', 'Frontier LLMs'] },
  { label: 'Infrastructure', items: ['Apple Silicon (M4 Ultra, 192GB)', 'SwiftUI', 'React', 'Web Scraping', 'Data Pipelines', 'Automated Filing'] },
  { label: 'Enterprise SaaS', items: ['ModMed (founding beta / product design)', 'EMR/CRM/PMS', 'SOP design & training'] },
];

const HERO_STATS = [
  { value: '200M+', label: 'Tokens per month' },
  { value: '11+',   label: 'Custom MCP integrations' },
  { value: '113K+', label: 'Cases analyzed' },
  { value: '5+',    label: 'AI platforms orchestrated' },
];

export default function Builder() {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="builder">
      {/* NAV */}
      <nav className="b-nav">
        <div className="b-nav-inner">
          <a href="/" className="b-nav-brand">
            <ARMark size={32} />
            <span className="b-nav-name">A.R. PRO SE</span>
          </a>
          <div className="b-nav-links">
            <a href="/" className="b-nav-link">← Home</a>
            <a href="/#services" className="b-nav-link">Services</a>
            <a href="/#contact" className="b-nav-cta">Get Access</a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="b-hero">
        <div className="b-hero-grid" aria-hidden="true" />
        <div className="b-hero-inner">
          <span className="b-section-tag">// Builder Portfolio</span>
          <h1 className="b-hero-h1">What one person<br /><span className="b-hero-accent">can build with AI.</span></h1>
          <p className="b-hero-sub">
            A.R. Pro Se started as a litigation support system. But the infrastructure behind it spans
            voice AI, local LLM deployment, enterprise software design, creative production, and
            cross-domain automation — all built by a single self-taught technologist processing
            200M+ tokens per month.
          </p>
          <div className="b-stats">
            {HERO_STATS.map(s => (
              <div className="b-stat" key={s.label}>
                <span className="b-stat-val">{s.value}</span>
                <span className="b-stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <div className="b-sections">
        {SECTIONS.map((sec) => (
          <section className={`b-section accent-${sec.accent}`} key={sec.num} id={sec.num}>
            <div className="b-section-inner">
              <div className="b-section-head">
                <div className="b-section-meta">
                  <span className="b-num">{sec.num}</span>
                  <span className="b-section-tag">{sec.label}</span>
                </div>
                <h2 className="b-section-title">{sec.title}</h2>
                <p className="b-section-desc">{sec.desc}</p>
              </div>

              <div className="b-items">
                {sec.items.map((item, i) => {
                  const key = `${sec.num}-${i}`;
                  return (
                    <div
                      className={`b-item ${expanded[key] ? 'open' : ''}`}
                      key={i}
                      onClick={() => toggle(key)}
                    >
                      <div className="b-item-header">
                        <span className="b-item-title">{item.title}</span>
                        <span className="b-item-arrow">{expanded[key] ? '−' : '+'}</span>
                      </div>
                      {expanded[key] && (
                        <p className="b-item-detail">{item.detail}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="b-tags">
                {sec.tags.map(t => <span className="b-tag" key={t}>{t}</span>)}
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* PHILOSOPHY */}
      <section className="b-philosophy">
        <div className="b-section-inner">
          <span className="b-section-tag">// Philosophy</span>
          <blockquote className="b-quote">
            "I don't use AI. I live inside it. The war machine and the birthday card come from the same infrastructure."
          </blockquote>
          <cite className="b-cite">— Abe Rosenwald</cite>
          <p className="b-philosophy-body">
            The three-volume musical, the Irish K-Pop demon hunter movie, the 113,000-case docket analysis,
            and the slime lab chemistry workbook for my nephew's birthday — that's what happens when a
            self-taught builder treats AI as infrastructure, not a novelty. I believe values determine
            long-term capability. I believe AI should keep humans accountable. And I believe the best
            way to understand what these tools can do is to push them further than anyone thought possible.
          </p>
        </div>
      </section>

      {/* TECH STACK */}
      <section className="b-stack">
        <div className="b-section-inner">
          <span className="b-section-tag">// Technical Stack</span>
          <h2 className="b-section-title">Full Infrastructure</h2>
          <div className="b-stack-grid">
            {STACK_GROUPS.map(g => (
              <div className="b-stack-group" key={g.label}>
                <div className="b-stack-label">{g.label}</div>
                <div className="b-stack-items">
                  {g.items.map(t => <span className="b-tag" key={t}>{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONNECT */}
      <section className="b-connect">
        <div className="b-section-inner b-connect-inner">
          <span className="b-section-tag">// Connect</span>
          <h2 className="b-section-title">Interested in what I'm building?</h2>
          <p className="b-connect-sub">
            Whether it's AI infrastructure, litigation support, or something I haven't thought of yet — I'm always building.
          </p>
          <div className="b-connect-links">
            <a href="https://linkedin.com/in/aberosenwald" className="b-connect-btn" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href="mailto:aberosenwald@icloud.com" className="b-connect-btn">Email</a>
            <a href="/" className="b-connect-btn b-connect-btn-primary">A.R. Pro Se Services →</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="b-footer">
        <div className="b-footer-inner">
          <div className="b-footer-brand">
            <ARMark size={28} />
            <span className="b-footer-name">A.R. PRO SE</span>
          </div>
          <div className="b-footer-copy">
            © 2026 Abraham Rosenwald — Built with AI infrastructure I control · April 2026
          </div>
        </div>
      </footer>
    </div>
  );
}
